import React, { useState, createContext, useContext, useEffect } from 'react';
import OversightSidebar from './components/OversightSidebar.js';
import OversightDashboard from './components/OversightDashboard.js';
import PerformanceRiskPanel from './components/PerformanceRiskPanel.js';
import StrategyAttributionPanel from './components/StrategyAttributionPanel.js';
import AuditCompliancePanel from './components/AuditCompliancePanel.js';
import LPOnboardingPanel from './components/LPOnboardingPanel.js';
import { AdminWorkspacePanel } from './components/AdminWorkspacePanel.js';
import StrategySelector from './components/StrategySelector.js';
import { useAuth } from '../../hooks/useAuth.js';

const enableRealBeeExecution = () => {
  if (typeof window !== 'undefined') {
    (window as any).__ARCOS_PRODUCTION_MODE__ = true;
    (window as any).__DISABLE_MOCK_TELEMETRY__ = true;
    console.log('[ARCOS] Production routing enabled - using real ARCOS routes');
  }
};

interface WorkspaceContextType {
  workspaceId: string;
  setWorkspaceId: (id: string) => void;
  availableWorkspaces: string[];
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspaceId: 'default',
  setWorkspaceId: () => {},
  availableWorkspaces: ['default']
});

export const useWorkspace = () => useContext(WorkspaceContext);

const ArcOversightPlugin: React.FC = () => {
  const { user, isLimitedPartner, canAccessFullData } = useAuth();
  const [activeTab, setActiveTab] = useState(isLimitedPartner() ? 'onboarding' : 'overview');
  const [selectedStrategy, setSelectedStrategy] = useState('tactical');
  const [isStrategyLocked, setIsStrategyLocked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [workspaceId, setWorkspaceId] = useState<string>('default');
  const [availableWorkspaces] = useState<string[]>(['default', 'LP_Alpha', 'LP_Beta', 'LP_Gamma']);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlWorkspaceId = urlParams.get('workspaceId');
    if (urlWorkspaceId && availableWorkspaces.includes(urlWorkspaceId)) {
      setWorkspaceId(urlWorkspaceId);
    }
    
    const userRole = localStorage.getItem('userRole');
    const adminToken = localStorage.getItem('adminToken');
    setIsAdmin(userRole === 'admin' || userRole === 'superadmin' || !!adminToken);
  }, [availableWorkspaces]);

  useEffect(() => {
    if (import.meta.env.MODE === 'production') {
      enableRealBeeExecution();
    }
  }, []);

  const handleWorkspaceChange = (newWorkspaceId: string) => {
    setWorkspaceId(newWorkspaceId);
    const url = new URL(window.location.href);
    url.searchParams.set('workspaceId', newWorkspaceId);
    window.history.pushState({}, '', url.toString());
  };

  const renderTabContent = () => {
    if (isLimitedPartner()) {
      switch (activeTab) {
        case 'onboarding':
          return <LPOnboardingPanel onComplete={() => setActiveTab('overview')} />;
        case 'overview':
          return <OversightDashboard />;
        case 'performance':
          return <PerformanceRiskPanel />;
        default:
          return <LPOnboardingPanel onComplete={() => setActiveTab('overview')} />;
      }
    }

    switch (activeTab) {
      case 'overview':
        return <OversightDashboard />;
      case 'performance':
        return <PerformanceRiskPanel />;
      case 'strategy':
        return (
          <div className="space-y-6">
            <StrategyAttributionPanel />
            {canAccessFullData() && (
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <StrategySelector
                    selectedStrategy={selectedStrategy}
                    onStrategyChange={setSelectedStrategy}
                    system="AEX"
                    isLocked={isStrategyLocked}
                  />
                  <StrategySelector
                    selectedStrategy={selectedStrategy}
                    onStrategyChange={setSelectedStrategy}
                    system="AEC"
                    isLocked={false}
                  />
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={() => setIsStrategyLocked(!isStrategyLocked)}
                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                      isStrategyLocked
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30'
                    }`}
                  >
                    {isStrategyLocked ? 'Unlock AEX Strategy' : 'Lock AEX Strategy'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case 'audit':
        return canAccessFullData() ? <AuditCompliancePanel /> : <OversightDashboard />;
      case 'admin':
        return isAdmin ? <AdminWorkspacePanel isAdmin={isAdmin} /> : <OversightDashboard />;
      default:
        return <OversightDashboard />;
    }
  };

  return (
    <WorkspaceContext.Provider value={{ workspaceId, setWorkspaceId: handleWorkspaceChange, availableWorkspaces }}>
      <div className="flex h-screen overflow-hidden">
        <OversightSidebar 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          userRole={user?.userType || 'admin'}
          isLimitedPartner={isLimitedPartner()}
          isAdmin={isAdmin}
        />
        <div className="flex-1 overflow-auto relative" style={{ backgroundColor: 'transparent' }}>
          {/* Unified Workspace Selector */}
          <div className="fixed top-4 right-4 z-40">
            <div className="bg-gradient-to-r from-slate-800/95 via-slate-800/90 to-slate-700/95 backdrop-blur-xl rounded-2xl border border-yellow-500/20 shadow-2xl overflow-hidden">
              <div className="flex items-center h-16">
                {/* Workspace Status Indicator */}
                <div className="flex items-center space-x-3 px-6 h-full">
                  <div className="relative">
                    <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse shadow-lg"></div>
                    <div className="absolute inset-0 w-3 h-3 bg-green-400/30 rounded-full animate-ping"></div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Active Workspace</span>
                    <span className="text-sm text-yellow-400 font-semibold">{workspaceId}</span>
                  </div>
                </div>

                {/* Subtle divider */}
                <div className="w-px h-8 bg-gradient-to-b from-transparent via-yellow-500/30 to-transparent"></div>

                {/* Enhanced Workspace Selector */}
                {availableWorkspaces.length > 1 && canAccessFullData() && (
                  <div className="relative group h-full">
                    <select
                      value={workspaceId}
                      onChange={(e) => handleWorkspaceChange(e.target.value)}
                      className="appearance-none bg-transparent text-yellow-400 font-semibold text-sm pl-6 pr-12 h-full focus:ring-0 focus:outline-none transition-all duration-300 cursor-pointer hover:bg-yellow-500/10 focus:bg-yellow-500/15 border-0 min-w-[140px]"
                    >
                      {availableWorkspaces.map(ws => (
                        <option key={ws} value={ws} className="bg-slate-800 text-slate-300 py-2">
                          {ws === 'default' ? '🏢 Default Workspace' : 
                           ws.startsWith('LP_') ? `👥 ${ws.replace('LP_', 'LP ')}` : 
                           `📊 ${ws}`}
                        </option>
                      ))}
                    </select>
                    
                    {/* Custom dropdown arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <svg className="w-4 h-4 text-yellow-400 group-hover:text-yellow-300 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/0 via-yellow-400/10 to-yellow-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sleek minimalist background image - slides up behind tabs */}
          <div 
            className="fixed inset-0 z-10"
            style={{
              backgroundImage: `url('/assets/oversight-background.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              left: '256px', // Account for sidebar width
              top: '0px' // Slide up behind workspace selector tabs
            }}
          />
          
          {/* Dark overlay for text readability */}
          <div className="fixed inset-0 z-15 bg-slate-900/0.3" style={{ left: '256px', top: '0px' }} />
          
          {/* Additional subtle golden overlay */}
          <div className="fixed inset-0 z-20 bg-gradient-to-br from-yellow-500/0.1 via-transparent to-yellow-600/0.1" style={{ left: '256px', top: '0px' }} />
          
          {/* Content layer */}
          <div className="relative z-30 min-h-full pt-20">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </WorkspaceContext.Provider>
  );
};

export default ArcOversightPlugin;
